export type VideoProviderName =
	| 'bunny'
	| 'vdocipher'
	| 'google_drive'
	| 'youtube';

/** How the backend masks a stored secret on read — the value never ships. */
export type MaskedSecret = { has_value: boolean; hint: string };

/**
 * Credentials record from GET: secret keys arrive masked, non-secret keys
 * (e.g. bunny fixed_library_id) arrive as plain strings, keys with no stored
 * value are absent entirely.
 */
export type MaskedCredentials = Record<string, MaskedSecret | string>;

/** Bunny Update-Video-Library fields stored as config.library_template. */
export type BunnyLibraryTemplate = {
	EnabledResolutions?: string;
	PlayerKeyColor?: string;
	FontFamily?: string;
	Controls?: string;
	PlaybackSpeeds?: string;
	EnableDRM?: boolean;
	EnableMP4Fallback?: boolean;
	KeepOriginalFiles?: boolean;
	AllowDirectPlay?: boolean;
	BlockNoneReferrer?: boolean;
	AllowedReferrers?: string[];
	RememberPlayerPosition?: boolean;
};

/** Defaults = snapshot of production library 720733 (Phase P spike). */
export const DEFAULT_BUNNY_LIBRARY_TEMPLATE: BunnyLibraryTemplate = {
	EnabledResolutions: '360p,480p,720p,1080p',
	PlayerKeyColor: '#23C9FC',
	FontFamily: 'lato',
	Controls:
		'rewind,fast-forward,play-large,current-time,duration,fullscreen,mute,play,progress,settings,volume',
	PlaybackSpeeds: '0.5,1,1.25,1.5,1.75,2',
	EnableDRM: true,
	EnableMP4Fallback: false,
	KeepOriginalFiles: false,
	AllowDirectPlay: false,
	BlockNoneReferrer: true,
	// Per-environment — set the current portal's domains, never copied between envs.
	AllowedReferrers: [],
	RememberPlayerPosition: true,
};

export type VideoProviderSetting = {
	provider: VideoProviderName;
	enabled: boolean;
	config: Record<string, any>;
	credentials: MaskedCredentials;
	/** False while VIDEO_PROVIDERS_ENCRYPTION_KEY is missing on the server. */
	encryption_ready: boolean;
};

/**
 * PUT payload. Secret semantics: omit a key to keep the stored value,
 * null to clear it, a plain string to set it. Config merges per key
 * (null deletes the key).
 */
export type UpdateVideoProviderPayload = {
	provider: VideoProviderName;
	enabled?: boolean;
	credentials?: Record<string, string | null>;
	config?: Record<string, any>;
};

/** video_provider_containers row (Lucid camelCase; apiKey never shipped). */
export type VideoProviderContainer = {
	id: number;
	provider: 'bunny' | 'vdocipher';
	kind: 'library' | 'collection' | 'folder';
	scopeType: 'instructor' | 'classroom' | 'course' | 'standalone_course';
	scopeId: number;
	externalId: string;
	parentId: number | null;
	createdAt: string;
	updatedAt: string;
};

export type PagedPayload = { page?: number; per_page?: number };

export type ApplyTemplateResult = {
	applied: number;
	failed: Array<{ library_id: string; error: string }>;
};

export type InstructorBunnyUsage = {
	library_id: string;
	storage_bytes: number;
	/** Month-to-date traffic from the Bunny library object. */
	streaming_bytes: number;
	video_count: number;
};

export type InstructorVdocipherUsage = {
	/** Daily-final accounting (~a day behind); null while unavailable. */
	streaming_bytes: number | null;
	/** Sum of imported source file sizes — approximation, not provider truth. */
	storage_bytes_approx: number;
	video_count: number;
};

export type InstructorUsageRow = {
	instructor_id: number;
	instructor_name: string;
	bunny: InstructorBunnyUsage | null;
	vdocipher: InstructorVdocipherUsage | null;
};

/** The usage endpoint's meta is {total, page, per_page} — not a Lucid paginator. */
export type UsageMeta = { total: number; page: number; per_page: number };
